import { Box, ListItemAvatar, Tooltip, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { IResponse } from '@work4all/data';

import { User } from '@work4all/models/lib/Classes/User.entity';

import {
  ListEntityPicker,
  ListEntityPickerInterface,
  ListEntityPickerWithoutTabsProps,
} from '../../../../../components/entity-picker/components/list-entity-picker';
import { HookedUserIcon } from '../../../../../components/user-icon/useUserIconRegister';

import { useMentionOptions } from './use-mention-options';

type MentionPickerInlineProps = Pick<
  MentionPickerInnerProps,
  'users' | 'onSelect' | 'pickerRef' | 'autoFocus'
>;

type MentionPickerMenuProps = Pick<MentionPickerInnerProps, 'onSelect'>;

interface MentionPickerInnerProps
  extends Pick<
    ListEntityPickerWithoutTabsProps<User, false>,
    'layout' | 'onSearchValueChange'
  > {
  users: User[];
  onSelect: (user: User) => void;
  pickerRef?: React.RefObject<ListEntityPickerInterface<User> | null>;
  autoFocus?: boolean;
}

function MentionPickerInner(props: MentionPickerInnerProps) {
  const { users, onSelect, layout, pickerRef, onSearchValueChange, autoFocus } =
    props;

  const dataset = useMemo<IResponse<User>>(() => {
    return {
      loading: false,
      pending: false,
      data: users,
      total: users.length,
      fetchMore: () => Promise.resolve(),
      refetch: () => Promise.resolve(null),
    };
  }, [users]);

  return (
    <Box sx={{ minWidth: 250 }}>
      <ListEntityPicker<User, false>
        autoFocus={autoFocus}
        pickerInterfaceRef={pickerRef}
        multiple={false}
        entity={null}
        data={null}
        filterBy={null}
        sortBy="name"
        layout={layout}
        suppressFilter
        fixedDataSet={dataset}
        onSearchValueChange={onSearchValueChange}
        value={null}
        onChange={(user) => {
          if (user != null) {
            onSelect(user);
          }
        }}
        renderItemContent={(user) => (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
            }}
          >
            <ListItemAvatar>
              <HookedUserIcon userId={user.id} size="l" />
            </ListItemAvatar>

            <Tooltip title={user.displayName}>
              <Typography noWrap>{user.displayName}</Typography>
            </Tooltip>
          </Box>
        )}
      />
    </Box>
  );
}

export function MentionPickerInline(props: MentionPickerInlineProps) {
  return <MentionPickerInner {...props} layout="simple" />;
}

export function MentionPickerMenu(props: MentionPickerMenuProps) {
  const [inputValue, setInputValue] = useState('');

  const users = useMentionOptions({
    text: inputValue,
  });

  return (
    <MentionPickerInner
      {...props}
      users={users}
      onSearchValueChange={setInputValue}
    />
  );
}
